import { CSVLink } from 'react-csv';
import { Link } from 'react-router-dom';
import {
  Access,
  AccessRule,
  AccessRuleConflictView,
  AccessRuleException,
  AccessRuleOwner,
  AccessRuleType,
  EmployeeType,
  User,
} from '@prisma/client';
import { Breadcrumb, Button, Dropdown, Input, Layout, Modal } from 'antd';
import Table from 'antd/es/table';

import { formatIsoDate } from '@app/utils-agnostic';
import { PageLayout, Tables } from '@app/web-components';

import { AuditFilters, CreatedByColumn } from '../../../common';
import { EmployeeTypeRenderer } from '../../../common/atoms/employee-type-renderer';
import { employeeSubtypeOptions } from '../../access/pages/rules/manage';
import { useFindAccessRules } from '../data/access-rule';
import { accessRuleIncludes, AccessRuleTypeRenderer, useAccessRuleActions } from '../libs';

type AccessRuleLandingItem = AccessRule & {
  access: Access;
  conflict: AccessRuleConflictView;
  owners: (AccessRuleOwner & { user: User })[];
  exceptions: (AccessRuleException & { user: User })[];
  createdByUser: User;
  updatedByUser: User;
};

export const AccessConflictLandingPage = () => {
  const { getDeleteModalProps, getDetailsModalProps, getDropdownButtonProps } = useAccessRuleActions({
    onCreate: (record) => (record ? `/admin/access/${record.accessId}/rules/create` : ''),
    onEdit: (record) => `./${record.id}`,
  });

  const {
    getCSVLinkProps,
    getParentLayoutContentProps,
    getQuery,
    getReloadButtonProps,
    getResetFiltersButtonProps,
    getSearchInputProps,
    getTableProps,
    getToolbarProps,
  } = Tables.useTableState<AccessRuleLandingItem>(
    'access_rule_access_conflicts',
    [
      {
        title: 'Action',
        width: 120,
        render: (_, record) => {
          const { menu, ...otherProps } = getDropdownButtonProps(record);
          return menu?.items && menu.items.length > 0 ? (
            <Dropdown.Button menu={menu} {...otherProps} />
          ) : (
            <Button {...otherProps} />
          );
        },
      },
      {
        title: 'Rule Type',
        dataIndex: ['conflict', 'ruleType'],
        ellipsis: true,
        sorter: true,
        width: 175,
        filters: [
          { text: AccessRuleTypeRenderer(AccessRuleType.ACCESS_CONFLICT), value: AccessRuleType.ACCESS_CONFLICT },
          { text: AccessRuleTypeRenderer(AccessRuleType.ACCESS_DEPENDENCY), value: AccessRuleType.ACCESS_DEPENDENCY },
          {
            text: AccessRuleTypeRenderer(AccessRuleType.DEPARTMENT_CONFLICT),
            value: AccessRuleType.DEPARTMENT_CONFLICT,
          },
        ],
        render: AccessRuleTypeRenderer,
      },
      {
        title: 'Access',
        dataIndex: ['access', 'name'],
        ellipsis: true,
        sorter: true,
        width: 400,
      },

      {
        title: 'Conflict',
        dataIndex: ['conflict', 'name'],
        ellipsis: true,
        sorter: true,
        width: 400,
      },
      {
        title: 'Employee Type',
        dataIndex: 'employeeType',
        sorter: true,
        width: 160,
        render: (employeeType: EmployeeType) => <EmployeeTypeRenderer employeeType={employeeType} />,
        filters: [
          { text: 'Intel', value: EmployeeType.INTEL },
          { text: 'CW', value: EmployeeType.CONTINGENT_WORKER },
        ],
      },
      {
        title: 'Employee Subtype',
        dataIndex: 'employeeSubtype',
        width: 185,
        sorter: true,
        filters: employeeSubtypeOptions,
      },
      CreatedByColumn,
      {
        title: 'Created On',
        dataIndex: 'createdOn',
        width: 165,
        ellipsis: true,
        render: (date: string) => formatIsoDate(date),
        sorter: true,
        defaultSortOrder: 'descend',
      },
    ],
    {
      customFilters: { 'createdByUser.name': AuditFilters['createdByUser.name'] },
      globalSearchFields: ['access.name', 'conflict.name'],
    },
  );

  const queryResult = useFindAccessRules<AccessRuleLandingItem>(
    getQuery({
      include: {
        ...accessRuleIncludes,
        createdByUser: true,
        updatedByUser: true,
      },
    }),
  );

  return (
    <>
      <Modal {...getDetailsModalProps()} />
      <Modal {...getDeleteModalProps()} />
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[{ title: <Link to="/">Home</Link> }, { title: 'Rules' }, { title: 'Access Rules' }]}
      />
      <Layout.Content {...getParentLayoutContentProps()}>
        <PageLayout.Title title="Access Rules" />

        <div
          {...getToolbarProps({
            items: [
              <Input {...getSearchInputProps()} />,
              <Button {...getReloadButtonProps()} onClick={() => queryResult.refetch()} />,
              <Button {...getResetFiltersButtonProps()} />,
              <CSVLink
                {...getCSVLinkProps(queryResult?.data?.data, {
                  'conflict.ruleType': AccessRuleTypeRenderer,
                })}
              />,
            ],
          })}
        />
        <Table {...getTableProps(queryResult)} />
      </Layout.Content>
    </>
  );
};
